import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { SNIPPET_CONFIG, generateSnippet } from '../utils/generateSnippets';

const ALL_LANG_IDS = Object.keys(SNIPPET_CONFIG);
const TAB_GAP = 4;

function CopyBtn({ text }) {
  const [done, setDone] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setDone(true);
      setTimeout(() => setDone(false), 1800);
    });
  };
  return (
    <button onClick={copy} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '4px 10px', fontSize: 11, fontWeight: 500,
      fontFamily: 'Inter, sans-serif', flexShrink: 0,
      border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer',
      background: done ? 'var(--success-bg)' : 'var(--bg-hover)',
      color: done ? 'var(--success)' : 'var(--text-muted)',
    }}>
      {done ? 'Copied!' : 'Copy'}
    </button>
  );
}

function highlightLine(line) {
  const tokens = [];
  const re = /("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*')|(--[\w-]+)|(\b(?:true|false|null)\b)|(\b\d+\b)|(\S+)|(\s+)/g;
  let firstWord = true;
  let m;
  while ((m = re.exec(line))) {
    const text = m[0];
    let color = 'var(--text-primary)';
    if (m[1]) color = 'var(--syn-string)';
    else if (m[2]) color = 'var(--syn-number)';
    else if (m[3]) color = 'var(--syn-bool)';
    else if (m[4]) color = 'var(--syn-number)';
    else if (m[5] && firstWord && /^(curl|wget|http|Invoke-RestMethod)$/i.test(text)) {
      color = 'var(--post)';
    }
    if (m[5]) firstWord = false;
    tokens.push(<span key={tokens.length} style={{ color }}>{text}</span>);
  }
  return tokens.length ? tokens : line;
}

function tabBtn(active) {
  return {
    padding: '6px 14px', borderRadius: 7, border: 'none', cursor: 'pointer',
    fontSize: 12.5, fontWeight: 500, fontFamily: 'Inter, sans-serif',
    background: active ? 'var(--bg-base)' : 'transparent',
    color: active ? 'var(--text-primary)' : 'var(--text-muted)',
    boxShadow: active ? 'var(--shadow-sm)' : 'none',
    whiteSpace: 'nowrap',
  };
}

function useVisibleTabCount(containerRef, measureRef) {
  const [visibleCount, setVisibleCount] = useState(ALL_LANG_IDS.length);

  const calc = useCallback(() => {
    const container = containerRef.current;
    const measureEl = measureRef.current;
    if (!container || !measureEl) return;

    const available = container.clientWidth;
    if (available <= 0) return;

    const tabEls = measureEl.querySelectorAll('[data-lang-measure]');
    const tabWidths = Array.from(tabEls).map(el => el.offsetWidth + TAB_GAP);

    let used = 0;
    let count = 0;
    for (const w of tabWidths) {
      if (used + w <= available) {
        used += w;
        count++;
      } else break;
    }

    // Keep at least one tab visible; if not everything fits, leave room for ··· sibling
    if (count >= ALL_LANG_IDS.length) {
      setVisibleCount(ALL_LANG_IDS.length);
    } else {
      setVisibleCount(Math.max(1, count));
    }
  }, [containerRef, measureRef]);

  useEffect(() => {
    const container = containerRef.current;
    const measureEl = measureRef.current;
    if (!container) return;

    const ro = new ResizeObserver(calc);
    ro.observe(container);
    if (measureEl) ro.observe(measureEl);
    calc();

    return () => ro.disconnect();
  }, [containerRef, measureRef, calc]);

  return visibleCount;
}

export default function RequestSnippets({ api }) {
  const { baseUrl, savedBearerKey, savedToken } = useApp();
  const [lang, setLang] = useState('shell');
  const [variant, setVariant] = useState('curl');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const tabsContainerRef = useRef(null);
  const measureRef = useRef(null);
  const visibleCount = useVisibleTabCount(tabsContainerRef, measureRef);

  const visibleLangIds = ALL_LANG_IDS.slice(0, visibleCount);
  const overflowLangIds = ALL_LANG_IDS.slice(visibleCount);

  const variants = SNIPPET_CONFIG[lang].variants;
  const isOverflowLang = overflowLangIds.includes(lang);

  const selectLang = (id) => {
    setLang(id);
    const first = Object.keys(SNIPPET_CONFIG[id].variants)[0];
    setVariant(first);
    setMenuOpen(false);
  };

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const code = useMemo(
    () => generateSnippet(api, lang, variant, { baseUrl, bearerKey: savedBearerKey, token: savedToken }),
    [api, lang, variant, baseUrl, savedBearerKey, savedToken]
  );

  return (
    <div style={{
      border: '1px solid var(--border)',
      borderRadius: 12,
      overflow: 'visible',
      background: 'var(--code-bg)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 14px 0',
        borderBottom: '1px solid var(--border)',
        background: 'var(--code-header)',
        borderRadius: '12px 12px 0 0',
        position: 'relative',
        zIndex: 2,
        overflow: 'visible',
      }}>
        <span style={{
          fontSize: 12, fontWeight: 700, color: 'var(--text-primary)',
          fontFamily: 'Inter, sans-serif', flexShrink: 0,
        }}>Request</span>

        <div
          ref={tabsContainerRef}
          style={{
            display: 'flex', alignItems: 'center', gap: TAB_GAP,
            flex: 1, minWidth: 0, overflow: 'hidden',
          }}
        >
          {visibleLangIds.map(id => (
            <button key={id} onClick={() => selectLang(id)} style={{ ...tabBtn(lang === id), flexShrink: 0 }}>
              {SNIPPET_CONFIG[id].label}
            </button>
          ))}
        </div>

        {/* Outside overflow:hidden so the dropdown is not clipped */}
        {overflowLangIds.length > 0 && (
          <div ref={menuRef} style={{ position: 'relative', zIndex: 30, flexShrink: 0 }}>
            <button
              onClick={() => setMenuOpen(o => !o)}
              style={{
                ...tabBtn(isOverflowLang || menuOpen),
                padding: isOverflowLang ? '6px 14px' : '6px 10px',
                fontSize: isOverflowLang ? 12.5 : 16,
                lineHeight: 1,
                letterSpacing: isOverflowLang ? 'normal' : '0.1em',
              }}
              title="More languages"
            >
              {isOverflowLang ? SNIPPET_CONFIG[lang].label : '···'}
            </button>
            {menuOpen && (
              <div style={{
                position: 'absolute',
                top: 'calc(100% + 6px)',
                right: 0,
                zIndex: 100,
                minWidth: 160,
                maxHeight: 280,
                overflowY: 'auto',
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                boxShadow: 'var(--shadow-lg)',
                padding: '6px 0',
              }}>
                {overflowLangIds.map(id => (
                  <button
                    key={id}
                    onClick={() => selectLang(id)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '8px 16px', border: 'none', cursor: 'pointer',
                      fontSize: 13, fontWeight: lang === id ? 600 : 400,
                      fontFamily: 'Inter, sans-serif',
                      background: lang === id ? 'var(--bg-hover)' : 'transparent',
                      color: lang === id ? 'var(--text-primary)' : 'var(--text-secondary)',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = lang === id ? 'var(--bg-hover)' : 'transparent'}
                  >
                    {SNIPPET_CONFIG[id].label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <CopyBtn text={code} />

        {/* Hidden measurer for responsive tab overflow */}
        <div
          ref={measureRef}
          aria-hidden
          style={{
            position: 'absolute', visibility: 'hidden', pointerEvents: 'none',
            display: 'flex', gap: TAB_GAP, whiteSpace: 'nowrap',
          }}
        >
          {ALL_LANG_IDS.map(id => (
            <button key={id} data-lang-measure style={tabBtn(false)}>
              {SNIPPET_CONFIG[id].label}
            </button>
          ))}
        </div>
      </div>

      {Object.keys(variants).length > 1 && (
        <div style={{ display: 'flex', gap: 4, padding: '8px 14px', background: 'var(--code-header)', borderBottom: '1px solid var(--border-subtle)' }}>
          {Object.entries(variants).map(([id, label]) => (
            <button key={id} onClick={() => setVariant(id)} style={{
              padding: '3px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
              fontSize: 11, fontWeight: 600, fontFamily: 'Inter, sans-serif',
              background: variant === id ? 'var(--bg-base)' : 'transparent',
              color: variant === id ? 'var(--text-primary)' : 'var(--text-muted)',
            }}>{label}</button>
          ))}
        </div>
      )}

      <pre style={{
        margin: 0, padding: '16px 18px', overflowX: 'auto',
        fontSize: 12, lineHeight: 1.7, fontFamily: 'JetBrains Mono, monospace',
        color: 'var(--text-primary)', maxHeight: 360,
      }}>
        {code.split('\n').map((line, i) => (
          <div key={i}>{highlightLine(line)}</div>
        ))}
      </pre>
    </div>
  );
}
